import { voolistExtension } from 'src/utils/voolist-extension';

/**
 * How long the confirming attempt waits for an answer. The first attempt uses the ping's own limit
 * (two seconds), which a healthy extension can miss while it is busy — a publish running in its
 * service worker, a worker Chrome is still starting — so the attempt that decides "no" waits longer.
 */
export const CONFIRMING_PING_TIMEOUT_MS = 5000;

/**
 * Can this browser act on a marketplace right now?
 *
 * Poshmark and Depop posts can only be created, edited or removed from inside the seller's own
 * browser, so every flow that hands work to the extension has to know whether the extension is
 * actually there — and has to ask at the moment it needs it, never remember an answer taken when
 * the page loaded, which is minutes stale and cached besides.
 *
 * Two attempts, because the two wrong answers do not cost the same. A false yes is the whole
 * silence budget of waiting per listing, with a claim left on every one of them; a false no
 * refuses a run that would have worked, or tells the seller the extension is gone while it is
 * working. Chrome shuts an idle extension's service worker down and the first ping is what wakes
 * it, and a busy extension can take longer than the first attempt allows, so a single short
 * attempt says "no" to a healthy extension often enough to matter.
 *
 * An extension that answers with words of its own has answered: it is there and cannot act, and
 * the words say why. That ends the check, and a second attempt would only make the seller wait for
 * the same words.
 *
 * @param {Object} [options]
 * @param {boolean} [options.confirmSilence=true] - Ask once more, more patiently, before a silence
 *   counts as "no". A caller that knows no extension can be there — a phone — passes false, and a
 *   silence is believed at once.
 *
 * @returns {Promise<{available: boolean, message: string|null, version: string|null}>} `message`
 *   is the extension's own words when it managed any, which is where "Extension was updated. Please
 *   refresh this page." arrives. `version` is the version of an extension that answered yes, and
 *   null otherwise: one that has stopped answering cannot say which version it was.
 */
export async function askExtensionIfItCanAct({ confirmSilence = true } = {}) {
  const first = await voolistExtension.ping();
  if (first?.installed) return { available: true, message: null, version: first.version || null };
  if (first?.message || !confirmSilence) {
    return { available: false, message: first?.message || null, version: null };
  }

  const second = await voolistExtension.ping({ timeoutMs: CONFIRMING_PING_TIMEOUT_MS });
  if (second?.installed) return { available: true, message: null, version: second.version || null };
  return { available: false, message: second?.message || null, version: null };
}

export default askExtensionIfItCanAct;
